import { useEffect, useRef, useState } from 'react';

import FormButton from '@/components/FormButton';
import { GRAPHQL_RESPONSE_EVENT } from '@/constants';
import { ImportTypeEnum } from '@/models/gen/graphql';
import Logger from '@/utils/logs';
import ReportProblemModal from '@/pages/Manifests/ReportProblem/ReportProblemModal';
import { captureScreenshot } from '@/utils';
import { createComponentState } from '@/state';
import reportImportProblem from '@/pages/Manifests/api/services/reportImportProblem';
import useEvent from '@/hooks/useEvent';

const log = Logger.of('ReportProblem');

/** preview page operations whose responses are included in a problem report */
export const TRACKED_OPERATIONS = new Set<string>([
  'RunPreviewImports',
  'RunIngestImports',
  'UpdateStagedTripBulk',
  'UpdateImportProvider',
  'CreateDownloadImportURLs',
  'DeleteImportBulk',
  'CreatePotentialTripBulk',
  'CreateFlagHasPotentialTripBulk',
  'SearchFlags',
]);

type GraphqlResponse = {
  operationName: string;
  variables?: Record<string, any>;
  data?: Record<string, any>;
  errors?: any[];
};
type ReportProblemState = {
  /** latest raw response per tracked operation, kept even when it contains errors */
  responses: Record<string, Omit<GraphqlResponse, 'operationName'> & { at: number }>;
  /** every error from tracked operations since the import was opened */
  errors: (Required<Omit<GraphqlResponse, 'data'>> & { at: number })[];
};

const initReportProblemState: ReportProblemState = { responses: {}, errors: [] };

export const useReportProblemState = createComponentState(initReportProblemState);

/** Records tracked preview responses for problem reports while mounted; starts over whenever the import changes. */
export const useReportProblemTracking = (importId: string): void => {
  useEffect(() => {
    const reset = (): void => useReportProblemState.setState((current) => ({ ...current, state: initReportProblemState }));
    reset();
    return reset;
  }, [importId]);

  useEvent(GRAPHQL_RESPONSE_EVENT, ({ operationName, variables = {}, data, errors }: GraphqlResponse): void => {
    if (!TRACKED_OPERATIONS.has(operationName)) return;
    const at = Date.now();
    const hasErrors = !!errors?.length;
    useReportProblemState.setState((current) => ({
      ...current,
      state: {
        responses: { ...current.state.responses, [operationName]: { variables, data, errors: hasErrors ? errors : undefined, at } },
        errors: hasErrors ? [...current.state.errors, { operationName, variables, errors, at }] : current.state.errors,
      },
    }));
  });
};

export type ReportProblemProps = {
  importId: string;
  importType: ImportTypeEnum;
  disabled?: boolean;
};

/**
 * ReportProblem
 *
 * Button that opens the Report a Problem modal for the current import preview. Captures the page before the modal
 * opens so the modal isn't in the image. Pair with useReportProblemTracking on the preview page.
 */
const ReportProblem = ({ importId, importType, disabled }: ReportProblemProps): JSX.Element => {
  const [show, setShow] = useState<boolean>(false);
  const [screenshot, setScreenshot] = useState<string>(undefined);
  // a ref, not state: a loading spinner on this button would end up in the screenshot
  const capturing = useRef<boolean>(false);

  const onOpen = async (): Promise<void> => {
    if (capturing.current) return;
    capturing.current = true;
    let image: string;
    try {
      image = await captureScreenshot();
    } catch (err) {
      // still allow reporting without a screenshot
      log.warn('failed to capture screenshot', err?.message || err);
    } finally {
      capturing.current = false;
    }
    setScreenshot(image);
    setShow(true);
  };

  const onSubmit = (message: string) => {
    const { responses, errors } = useReportProblemState.getState().state;
    return reportImportProblem({
      importId,
      importType,
      message,
      screenshot,
      responses: JSON.stringify(responses),
      errors: JSON.stringify(errors),
      url: window.location.href,
      userAgent: navigator.userAgent,
      capturedAt: new Date().toISOString(),
    });
  };

  return (
    <>
      <FormButton
        icon={<i className="sv sv-warning fs-4" />}
        onClick={onOpen}
        disabled={disabled}
        feedback="Report a problem"
        aria-label="Report a problem"
      />
      <ReportProblemModal show={show} screenshot={screenshot} onHide={(): void => setShow(false)} onSubmit={onSubmit} />
    </>
  );
};

export default ReportProblem;
